<template>
    <el-dialog
        :visible="visible"
        :title="isEdit ? 'Chỉnh sửa order tuyển dụng' : 'Thêm mới order tuyển dụng'"
        width="760px"
        custom-class="ro-dialog ro-form-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="close"
        @closed="onClosed"
    >
        <div slot="title" class="ro-dialog-header">
            <div class="ro-dialog-header__icon" :class="{ 'is-edit': isEdit }">
                <i :class="isEdit ? 'el-icon-edit-outline' : 'el-icon-document-add'"></i>
            </div>
            <div class="ro-dialog-header__info">
                <h3 class="ro-dialog-header__title">
                    {{ isEdit ? 'Chỉnh sửa order tuyển dụng' : 'Thêm mới order tuyển dụng' }}
                </h3>
                <p class="ro-dialog-header__subtitle">
                    {{ isEdit ? `Cập nhật thông tin yêu cầu tuyển dụng #${orderData ? orderData.id : ''}` : 'Điền đầy đủ thông tin để gửi yêu cầu tuyển dụng nhân sự' }}
                </p>
            </div>
        </div>

        <el-form
            ref="formRef"
            label-position="top"
            size="small"
            :model="form"
            :rules="rules"
            class="ro-form"
        >
            <!-- 1. Loại tuyển dụng (Radio Card Selector) -->
            <div class="ro-form-section">
                <div class="ro-form-section__head">
                    <i class="el-icon-connection"></i>
                    <span>Hình thức tuyển dụng</span>
                </div>
                <div class="ro-type-grid">
                    <div
                        v-for="opt in orderTypeOptions"
                        :key="opt.value"
                        class="ro-type-card"
                        :class="{
                            'is-active': form.type === opt.value,
                            'is-teacher': opt.value === 2
                        }"
                        @click="selectType(opt.value)"
                    >
                        <div class="ro-type-card__icon">
                            <i :class="opt.value === 2 ? 'el-icon-reading' : 'el-icon-office-building'"></i>
                        </div>
                        <div class="ro-type-card__content">
                            <div class="ro-type-card__title">{{ opt.label }}</div>
                            <div class="ro-type-card__desc">
                                {{ opt.value === 2 ? 'Tuyển dụng cho khối Giáo viên (TCGV)' : 'Tuyển dụng nhân sự cho các phòng ban' }}
                            </div>
                        </div>
                        <div class="ro-type-card__badge">
                            <i class="el-icon-check"></i>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 2. Thông tin vị trí & Tiêu đề -->
            <div class="ro-form-section">
                <div class="ro-form-section__head">
                    <i class="el-icon-suitcase"></i>
                    <span>Thông tin vị trí tuyển dụng</span>
                </div>

                <el-row :gutter="14">
                    <el-col :span="12">
                        <el-form-item label="Phân loại" prop="recruitment_job_category_id" required>
                            <el-select
                                v-model="form.recruitment_job_category_id"
                                placeholder="Chọn phân loại..."
                                class="w-100"
                                clearable
                                filterable
                                :loading="loadingCatalogs"
                                @change="onCategoryChange"
                            >
                                <el-option
                                    v-for="cat in jobCatalogs"
                                    :key="cat.id"
                                    :label="cat.name"
                                    :value="cat.id"
                                >
                                    <span>{{ cat.name }}</span>
                                    <span v-if="cat.details && cat.details.length" class="ro-catalog-badge">
                                        {{ cat.details.length }} chi tiết
                                    </span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item label="Chi tiết">
                            <el-select
                                v-if="hasJobDetails"
                                v-model="form.recruitment_job_detail_id"
                                placeholder="Chọn chi tiết từ mẫu..."
                                class="w-100"
                                clearable
                                filterable
                                @change="onJobDetailChange"
                            >
                                <el-option
                                    v-for="d in currentJobDetails"
                                    :key="d.id"
                                    :label="d.name"
                                    :value="d.id"
                                />
                            </el-select>
                            <el-input
                                v-else
                                v-model="form.recruitment_job_detail_name"
                                placeholder="Tự nhập tên Job chi tiết..."
                                clearable
                                class="w-100"
                                @input="onJobDetailNameInput"
                            />
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- Mã đơn tuyển dụng (Bắt buộc đối với job Tuyển hộ / KHSX) -->
                <el-row v-if="isTeacherOrder" :gutter="14">
                    <el-col :span="24">
                        <el-form-item label="Mã đơn" prop="code_order" required>
                            <el-input
                                v-model="form.code_order"
                                placeholder="Nhập mã đơn tuyển hộ (bắt buộc)..."
                                maxlength="100"
                                clearable
                            >
                                <i slot="prefix" class="el-input__icon el-icon-tickets"></i>
                            </el-input>
                            <div class="ro-form-hint">
                                Bắt buộc nhập mã đơn đối với yêu cầu tuyển dụng của khối Tuyển hộ (KHSX).
                            </div>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- Tiêu đề yêu cầu tuyển dụng -->
                <el-form-item label="Tiêu đề yêu cầu tuyển dụng" prop="title" required>
                    <el-input
                        v-model="form.title"
                        placeholder="VD: Tuyển Chuyên viên tư vấn tuyển sinh / Giáo viên IM HN/HCM..."
                        maxlength="255"
                        show-word-limit
                        clearable
                    />
                </el-form-item>
            </div>

            <!-- 3. Địa điểm & Hình thức làm việc -->
            <div class="ro-form-section">
                <div class="ro-form-section__head">
                    <i class="el-icon-location-outline"></i>
                    <span>Địa điểm & Hình thức làm việc</span>
                </div>

                <el-row :gutter="14">
                    <el-col :span="12">
                        <el-form-item label="Địa điểm làm việc" prop="branch_id">
                            <el-select
                                v-model="form.branch_id"
                                filterable
                                clearable
                                :loading="loadingBranches"
                                placeholder="Chọn địa điểm / cơ sở làm việc..."
                                class="w-100"
                            >
                                <el-option
                                    v-for="b in branchOptions"
                                    :key="b.id"
                                    :label="branchLabel(b)"
                                    :value="b.id"
                                >
                                    <div class="ro-branch-opt">
                                        <span class="ro-branch-opt__name">{{ b.name || b.branch_name || b.title || `Cơ sở #${b.id}` }}</span>
                                        <span v-if="b.address" class="ro-branch-opt__address" :title="b.address">
                                            ({{ b.address }})
                                        </span>
                                    </div>
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item label="Hình thức làm việc" prop="work_type">
                            <el-select
                                v-model="form.work_type"
                                clearable
                                filterable
                                :loading="loadingWorkForms"
                                placeholder="Chọn hình thức làm việc..."
                                class="w-100"
                            >
                                <el-option
                                    v-for="wf in filteredWorkForms"
                                    :key="wf.id"
                                    :label="wf.name"
                                    :value="wf.id"
                                />
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
            </div>

            <!-- 4. Kế hoạch thời gian & Số lượng -->
            <div class="ro-form-section">
                <div class="ro-form-section__head">
                    <i class="el-icon-date"></i>
                    <span>Thời gian & Số lượng</span>
                </div>

                <el-row :gutter="14">
                    <el-col :span="8">
                        <el-form-item label="Thời gian bắt đầu" prop="start_date">
                            <el-date-picker
                                v-model="form.start_date"
                                type="date"
                                value-format="yyyy-MM-dd"
                                format="dd/MM/yyyy"
                                placeholder="Ngày bắt đầu"
                                class="w-100"
                                @change="onStartDateChange"
                            />
                        </el-form-item>
                    </el-col>

                    <el-col :span="8">
                        <el-form-item label="Thời gian kết thúc" prop="end_date">
                            <el-date-picker
                                v-model="form.end_date"
                                type="date"
                                value-format="yyyy-MM-dd"
                                format="dd/MM/yyyy"
                                placeholder="Hạn kết thúc"
                                class="w-100"
                                :picker-options="endDatePickerOptions"
                            />
                        </el-form-item>
                    </el-col>

                    <el-col :span="8">
                        <el-form-item label="Số lượng cần tuyển" prop="quantity" required>
                            <el-input-number
                                v-model="form.quantity"
                                :min="1"
                                :max="999"
                                controls-position="right"
                                placeholder="Số lượng"
                                class="w-100"
                            />
                        </el-form-item>
                    </el-col>
                </el-row>
            </div>

            <!-- 5. Mô tả yêu cầu tuyển dụng -->
            <div class="ro-form-section">
                <div class="ro-form-section__head">
                    <i class="el-icon-notebook-2"></i>
                    <span>Mô tả công việc & Yêu cầu tuyển dụng</span>
                </div>

                <el-form-item prop="description" class="mb-0">
                    <el-input
                        v-model="form.description"
                        type="textarea"
                        :rows="4"
                        placeholder="Nhập chi tiết mô tả công việc, trách nhiệm chính, yêu cầu ứng viên, chế độ đãi ngộ..."
                        maxlength="2000"
                        show-word-limit
                    />
                </el-form-item>
            </div>

            <!-- 5. Phân công nhân sự phụ trách -->
            <div
                v-if="isTeacherOrder || (isEdit && isApproved && canAssignPersonInCharge) || (canAssignCvApprover && isTeacherOrder && !isRejected)"
                class="ro-form-section"
            >
                <div class="ro-form-section__head">
                    <i class="el-icon-user"></i>
                    <span>Phân công nhân sự phụ trách</span>
                </div>

                <el-row :gutter="14">
                    <!-- Đối với job tuyển hộ: KHSX tạo thì có thể chọn Trưởng nhóm phụ trách luôn -->
                    <el-col v-if="isTeacherOrder" :span="(canAssignCvApprover && isTeacherOrder && !isRejected && isEdit) ? 12 : 24">
                        <el-form-item label="Trưởng nhóm phụ trách">
                            <el-select
                                v-model="form.person_in_charge_id"
                                filterable
                                clearable
                                :loading="loadingTeacherGroups"
                                placeholder="Chọn trưởng nhóm phụ trách (KHSX có thể chỉ định luôn)..."
                                class="w-100"
                            >
                                <el-option
                                    v-for="u in teacherGroupOptions"
                                    :key="u.id"
                                    :label="userOptionLabel(u)"
                                    :value="u.id"
                                >
                                    <span>{{ u.name }}</span>
                                    <span v-if="u.department_name || u.team_name || u.email" class="ro-user-sub">
                                        {{ u.department_name || u.team_name || u.email }}
                                    </span>
                                </el-option>
                            </el-select>
                            <div class="ro-form-hint">
                                KHSX có thể chỉ định trưởng nhóm phụ trách luôn khi tạo. Nếu để trống, khi duyệt HR Lead (chị Ngà) sẽ bắt buộc chỉ định Người điều phối để gán sau.
                            </div>
                        </el-form-item>
                    </el-col>

                    <!-- Đối với order tuyển chính thức: HR phụ trách khi đã duyệt -->
                    <el-col v-else-if="isEdit && isApproved && canAssignPersonInCharge" :span="24">
                        <el-form-item label="HR phụ trách">
                            <el-select
                                v-model="form.person_in_charge_id"
                                filterable
                                clearable
                                placeholder="Chọn HR phụ trách..."
                                class="w-100"
                            >
                                <el-option
                                    v-for="u in hrTeamOptions"
                                    :key="u.id"
                                    :label="userOptionLabel(u)"
                                    :value="u.id"
                                >
                                    <span>{{ u.name }}</span>
                                    <span v-if="u.department_name || u.team_name || u.email" class="ro-user-sub">
                                        {{ u.department_name || u.team_name || u.email }}
                                    </span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>

                    <!-- Người duyệt CV (chỉ áp dụng cho job tuyển hộ khi chỉnh sửa) -->
                    <el-col v-if="canAssignCvApprover && isTeacherOrder && !isRejected && isEdit" :span="12">
                        <el-form-item label="Người duyệt CV" prop="cv_approver_id">
                            <el-select
                                v-model="form.cv_approver_id"
                                filterable
                                remote
                                clearable
                                :remote-method="searchUsers"
                                :loading="searchingUsers"
                                placeholder="Tìm theo tên, email để chọn người duyệt CV..."
                                class="w-100"
                            >
                                <el-option
                                    v-for="u in userOptions"
                                    :key="u.id"
                                    :label="userOptionLabel(u)"
                                    :value="u.id"
                                >
                                    <span>{{ u.name }}</span>
                                    <span v-if="u.department_name || u.team_name || u.email" class="ro-user-sub">
                                        {{ u.department_name || u.team_name || u.email }}
                                    </span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
            </div>
        </el-form>

        <div slot="footer" class="ro-dialog-footer">
            <el-button size="small" class="ro-btn-cancel" @click="close">
                Huỷ
            </el-button>

            <div class="ro-dialog-footer__right">
                <template v-if="isEdit">
                    <!-- HR Duyệt & Từ chối (chỉ account_type_id = 16 & department_id = 3) -->
                    <template v-if="canApproveHr">
                        <el-button
                            v-if="orderData && orderData.hr_approve != 2"
                            size="small"
                            type="danger"
                            plain
                            icon="el-icon-close"
                            :loading="submitting"
                            @click="handleApprove('hr', 2)"
                        >
                            HR Từ chối
                        </el-button>
                        <el-button
                            v-if="orderData && orderData.hr_approve != 1"
                            size="small"
                            type="success"
                            icon="el-icon-check"
                            :loading="submitting"
                            @click="handleApprove('hr', 1)"
                        >
                            HR Duyệt
                        </el-button>
                    </template>

                    <!-- Teamlead Duyệt & Từ chối (chỉ account_type_id = 16 & department_id = 52) -->
                    <template v-if="canApproveTeamlead">
                        <el-button
                            v-if="orderData && orderData.teamlead_approve != 2"
                            size="small"
                            type="danger"
                            plain
                            icon="el-icon-close"
                            :loading="submitting"
                            @click="handleApprove('teamlead', 2)"
                        >
                            Teamlead Từ chối
                        </el-button>
                        <el-button
                            v-if="orderData && orderData.teamlead_approve != 1"
                            size="small"
                            type="success"
                            icon="el-icon-check"
                            :loading="submitting"
                            @click="handleApprove('teamlead', 1)"
                        >
                            Teamlead Duyệt
                        </el-button>
                    </template>
                </template>

                <el-button
                    size="small"
                    type="primary"
                    :loading="submitting"
                    icon="el-icon-check"
                    class="ro-btn-submit"
                    @click="submit"
                >
                    {{ isEdit ? "Lưu thay đổi" : "Tạo order tuyển dụng" }}
                </el-button>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import {
    TYPE_TUYEN_CHINH_THUC,
    TYPE_TUYEN_HO,
    orderTypeOptionsFor,
    ORDER_TYPE_META,
    isOrderApproved,
    isOrderRejected,
    resolveDepartmentIds,
    DEPARTMENT_TCGV,
    canUserApproveHr,
    canUserApproveTeamlead,
} from "@/core/config/recruitmentOrders/recruitmentOrdersOption";
import { mapGetters } from "vuex";
import {
    RO_GET_TEACHER_GROUPS,
    RO_GET_JOB_CATALOG,
    RO_GET_BRANCHES,
    RO_GET_HR_TEAMLEADS,
    RO_GET_WORK_FORMS,
} from "@/core/services/store/recruitment-orders/recruitmentOrders.module";

function blankForm(allowTeacher = false) {
    return {
        id: null,
        title: "",
        description: "",
        type: allowTeacher ? TYPE_TUYEN_HO : TYPE_TUYEN_CHINH_THUC,
        recruitment_job_category_id: null,
        recruitment_job_detail_id: null,
        recruitment_job_detail_name: "",
        branch_id: null,
        work_type: null,
        start_date: "",
        end_date: "",
        quantity: 1,
        code_order: "",
        person_in_charge_id: null,
        cv_approver_id: null,
    };
}

export default {
    name: "OrderFormDialog",
    props: {
        visible: { type: Boolean, default: false },
        orderData: { type: Object, default: null },
        submitting: { type: Boolean, default: false },
        /** Người đang đăng nhập thuộc department_id = 44 -> được chọn Tuyển hộ (Giáo viên) */
        allowTeacherType: { type: Boolean, default: false },
        /** Người đang đăng nhập thuộc phòng HR (department_id = 3) -> được gán người duyệt CV */
        canAssignCvApprover: { type: Boolean, default: false },
        /** Chỉ account_type_id = 16 của department_id = 3 mới được gán HR phụ trách */
        canAssignPersonInCharge: { type: Boolean, default: false },
        /** Thông tin currentUser */
        currentUser: { type: Object, default: null },
    },
    data() {
        return {
            form: blankForm(this.allowTeacherType),
            jobCatalogs: [],
            loadingCatalogs: false,
            branchOptions: [],
            loadingBranches: false,
            userOptions: [],
            searchingUsers: false,
            teacherGroupOptions: [],
            loadingTeacherGroups: false,
            debounceTimer: null,
            prevDetailName: "",
            workForms: [],
            loadingWorkForms: false,
            rules: {
                recruitment_job_category_id: [
                    { required: true, message: "Vui lòng chọn Phân loại", trigger: "change" },
                ],
                title: [
                    { required: true, message: "Vui lòng nhập Tiêu đề yêu cầu tuyển dụng", trigger: "blur" },
                    { max: 255, message: "Tiêu đề tối đa 255 ký tự", trigger: "blur" },
                ],
                type: [
                    { required: true, message: "Vui lòng chọn Hình thức tuyển dụng", trigger: "change" },
                ],
                code_order: [
                    {
                        validator: (rule, value, callback) => {
                            if (Number(this.form.type) === TYPE_TUYEN_HO) {
                                if (!value || !String(value).trim()) {
                                    return callback(new Error("Vui lòng nhập Mã đơn"));
                                }
                            }
                            callback();
                        },
                        trigger: ["blur", "change"],
                    },
                ],
                quantity: [
                    { required: true, message: "Vui lòng nhập Số lượng cần tuyển", trigger: "change" },
                ],
                end_date: [
                    {
                        validator: (rule, value, callback) => {
                            // Thời gian kết thúc không được nhỏ hơn thời gian bắt đầu (chỉ dùng cho tuyển hộ)
                            if (Number(this.form.type) === TYPE_TUYEN_HO && this.form.start_date && value) {
                                const start = new Date(this.form.start_date).getTime();
                                const end = new Date(value).getTime();
                                if (end < start) {
                                    return callback(new Error("Thời gian kết thúc không được nhỏ hơn thời gian bắt đầu"));
                                }
                            }
                            callback();
                        },
                        trigger: "change",
                    },
                ],
            },
        };
    },
    computed: {
        ...mapGetters(["roHrTeamLeads", "roWorkForms"]),
        filteredWorkForms() {
            // Tuyển chính thức (type = 1) dùng các bản ghi type = 1, Tuyển hộ (type = 2) dùng các bản ghi type = 2
            const targetType = Number(this.form.type) === TYPE_TUYEN_HO ? 2 : 1;
            const sourceList = (this.workForms && this.workForms.length > 0) ? this.workForms : (this.roWorkForms || []);
            const list = sourceList.filter((w) => Number(w.type) === targetType);

            // Đảm bảo nếu đang sửa hoặc form.work_type đã có giá trị thì hiển thị được label trong el-select
            if (this.form.work_type !== null && this.form.work_type !== undefined && this.form.work_type !== "") {
                const exists = list.some((w) => Number(w.id) === Number(this.form.work_type));
                if (!exists) {
                    let fallbackName = "";
                    if (this.orderData && Number(this.orderData.work_type) === Number(this.form.work_type)) {
                        fallbackName =
                            (this.orderData.work_form && this.orderData.work_form.name) ||
                            this.orderData.work_type_name ||
                            this.orderData.working_form_name ||
                            "";
                    }
                    if (!fallbackName) {
                        const inAll = sourceList.find((w) => Number(w.id) === Number(this.form.work_type));
                        if (inAll) fallbackName = inAll.name;
                    }
                    if (fallbackName) {
                        return [
                            ...list,
                            { id: Number(this.form.work_type), name: fallbackName, type: targetType }
                        ];
                    }
                }
            }
            return list;
        },
        hrTeamOptions() {
            let list = Array.isArray(this.roHrTeamLeads) ? [...this.roHrTeamLeads] : [];
            list = list.filter((u) => {
                if (!u) return false;
                const accId = u.account_type_id || (u.account_type && u.account_type.id);
                if (accId !== undefined && accId !== null && Number(accId) !== 16) return false;
                const deptId = u.department_id || (u.department && u.department.id);
                if (deptId !== undefined && deptId !== null && Number(deptId) !== 3) return false;
                return true;
            });
            if (this.orderData && this.orderData.person_in_charge && this.orderData.person_in_charge.id) {
                if (!list.some((u) => String(u.id) === String(this.orderData.person_in_charge.id))) {
                    list.unshift(this.orderData.person_in_charge);
                }
            }
            return list;
        },
        isEdit() {
            return !!(this.orderData && this.orderData.id);
        },
        canApproveHr() {
            return canUserApproveHr(this.currentUser);
        },
        canApproveTeamlead() {
            return canUserApproveTeamlead(this.currentUser);
        },
        isApproved() {
            return isOrderApproved(this.orderData);
        },
        isRejected() {
            return isOrderRejected(this.orderData);
        },
        isTeacherOrder() {
            return Number(this.form.type) === TYPE_TUYEN_HO;
        },
        /**
         * Hiện lựa chọn "Tuyển hộ (Giáo viên)" khi người dùng thuộc TCGV, hoặc khi
         * đang sửa một order vốn đã là loại này.
         */
        showTeacherType() {
            return this.allowTeacherType || Number(this.form.type) === TYPE_TUYEN_HO;
        },
        orderTypeOptions() {
            return orderTypeOptionsFor(this.showTeacherType);
        },
        typeMeta() {
            return ORDER_TYPE_META[this.form.type] || ORDER_TYPE_META[TYPE_TUYEN_CHINH_THUC];
        },
        selectedCategory() {
            if (!this.form.recruitment_job_category_id) return null;
            return this.jobCatalogs.find((c) => String(c.id) === String(this.form.recruitment_job_category_id)) || null;
        },
        currentJobDetails() {
            return this.selectedCategory && Array.isArray(this.selectedCategory.details)
                ? this.selectedCategory.details
                : [];
        },
        hasJobDetails() {
            if (!this.selectedCategory) return false;
            if (this.selectedCategory.has_details === false) return false;
            return this.currentJobDetails.length > 0;
        },
        endDatePickerOptions() {
            return {
                disabledDate: (time) => {
                    // Đối với Tuyển hộ, vô hiệu hoá các ngày trước start_date
                    if (Number(this.form.type) === TYPE_TUYEN_HO && this.form.start_date) {
                        const start = new Date(this.form.start_date);
                        start.setHours(0, 0, 0, 0);
                        return time.getTime() < start.getTime();
                    }
                    return false;
                },
            };
        },
    },
    watch: {
        orderData: {
            immediate: true,
            handler(val) {
                if (val && val.id) {
                    const picId = val.person_in_charge_id || (val.person_in_charge && val.person_in_charge.id) || null;
                    const approverId = val.cv_approver_id || (val.cv_approver && val.cv_approver.id) || null;
                    const catId = val.recruitment_job_category_id || val.job_catalog_id || val.catalog_id || val.category_id || null;
                    const detailId = val.recruitment_job_detail_id || val.job_detail_id || val.detail_id || null;
                    const detailName = val.recruitment_job_detail_name || val.job_detail || val.job_detail_name || "";
                    const branchId = val.branch_id || (val.branch && val.branch.id) || null;
                    const rawWorkType = (val.work_form && val.work_form.id !== undefined && val.work_form.id !== null)
                        ? val.work_form.id
                        : (val.work_type !== undefined && val.work_type !== null && val.work_type !== ""
                            ? val.work_type
                            : (val.working_form !== undefined && val.working_form !== null && val.working_form !== "" ? val.working_form : null));
                    const workType = rawWorkType !== null && rawWorkType !== undefined
                        ? (isNaN(Number(rawWorkType)) ? rawWorkType : Number(rawWorkType))
                        : null;
                    const startDate = val.start_date || val.start_time || "";
                    const endDate = val.end_date || val.end_time || "";
                    const qty = val.quantity !== undefined && val.quantity !== null ? Number(val.quantity) : (val.amount || 1);

                    this.form = {
                        id: val.id,
                        title: val.title || val.position_title || "",
                        description: val.description || "",
                        type: Number(val.type) || (val.order_type === "teacher" || val.order_type === 2 ? TYPE_TUYEN_HO : TYPE_TUYEN_CHINH_THUC),
                        recruitment_job_category_id: catId ? Number(catId) : null,
                        recruitment_job_detail_id: detailId ? Number(detailId) : null,
                        recruitment_job_detail_name: detailName,
                        branch_id: branchId ? Number(branchId) : null,
                        work_type: workType,
                        start_date: startDate,
                        end_date: endDate,
                        quantity: qty,
                        code_order: val.code_order || "",
                        person_in_charge_id: picId ? Number(picId) : null,
                        cv_approver_id: approverId ? Number(approverId) : null,
                        hiring_start_date: val.hiring_start_date || "",
                    };
                    this.prevDetailName = detailName;
                    const initialUsers = [];
                    if (val.person_in_charge && val.person_in_charge.id) {
                        initialUsers.push(val.person_in_charge);
                    }
                    if (val.cv_approver && val.cv_approver.id) {
                        if (!initialUsers.some((u) => String(u.id) === String(val.cv_approver.id))) {
                            initialUsers.push(val.cv_approver);
                        }
                    }
                    this.userOptions = initialUsers;
                    if (val.branch && val.branch.id) {
                        this.branchOptions = [val.branch];
                    }
                } else {
                    this.form = blankForm(this.allowTeacherType);
                    this.prevDetailName = "";
                    this.form.person_in_charge_id = null;
                    this.form.cv_approver_id = null;
                    this.userOptions = [];
                }
            },
        },
        visible(val) {
            if (val) {
                if (!this.isEdit) {
                    this.form.type = this.allowTeacherType ? TYPE_TUYEN_HO : TYPE_TUYEN_CHINH_THUC;
                }
                if (this.jobCatalogs.length === 0) {
                    this.fetchJobCatalogs();
                }
                if (this.branchOptions.length === 0) {
                    this.fetchBranches();
                }
                if (this.workForms.length === 0) {
                    this.fetchWorkForms();
                }
                if (!this.roHrTeamLeads || this.roHrTeamLeads.length === 0) {
                    this.$store.dispatch(RO_GET_HR_TEAMLEADS).catch(() => {});
                }
                if (this.userOptions.length === 0) {
                    this.fetchUsers("");
                }
                if (Number(this.form.type) === TYPE_TUYEN_HO && this.teacherGroupOptions.length === 0) {
                    this.fetchTeacherGroups();
                }
            }
        },
    },
    mounted() {
        this.fetchJobCatalogs();
        this.fetchBranches();
        this.fetchWorkForms();
        if (Number(this.form.type) === TYPE_TUYEN_HO) {
            this.fetchTeacherGroups();
        }
    },
    methods: {
        close() {
            this.$emit("update:visible", false);
        },
        onClosed() {
            clearTimeout(this.debounceTimer);
            this.form = blankForm(this.allowTeacherType);
            this.prevDetailName = "";
            this.userOptions = [];
            if (this.$refs.formRef) this.$refs.formRef.clearValidate();
        },
        selectType(typeVal) {
            const oldType = this.form.type;
            this.form.type = typeVal;
            if (Number(oldType) !== Number(typeVal)) {
                this.validateOrResetWorkType();
            }
            this.onTypeChange();
        },
        validateOrResetWorkType() {
            if (this.form.work_type === null || this.form.work_type === undefined || this.form.work_type === "") {
                return;
            }
            const expectedType = Number(this.form.type) === TYPE_TUYEN_HO ? 2 : 1;
            const sourceList = (this.workForms && this.workForms.length > 0) ? this.workForms : (this.roWorkForms || []);
            const isValid = sourceList.some(
                (w) => Number(w.id) === Number(this.form.work_type) && Number(w.type) === expectedType
            );
            if (!isValid) {
                this.form.work_type = null;
            }
        },
        onTypeChange() {
            if (this.$refs.formRef) {
                this.$refs.formRef.clearValidate(["type", "code_order"]);
                if (Number(this.form.type) === TYPE_TUYEN_HO && this.form.code_order) {
                    this.$refs.formRef.validateField("code_order");
                }
                if (this.form.end_date) {
                    this.$refs.formRef.validateField("end_date");
                }
            }
            if (Number(this.form.type) === TYPE_TUYEN_HO && this.teacherGroupOptions.length === 0) {
                this.fetchTeacherGroups();
            }
        },
        fetchTeacherGroups(keyword = "") {
            this.loadingTeacherGroups = true;
            this.$store
                .dispatch(RO_GET_TEACHER_GROUPS, { keyword })
                .then((list) => {
                    const results = Array.isArray(list) ? list : [];
                    if (this.form.person_in_charge_id) {
                        const existing =
                            this.teacherGroupOptions.find((u) => String(u.id) === String(this.form.person_in_charge_id)) ||
                            (this.orderData && this.orderData.person_in_charge && String(this.orderData.person_in_charge.id) === String(this.form.person_in_charge_id)
                                ? this.orderData.person_in_charge
                                : null);
                        if (existing && !results.some((u) => String(u.id) === String(this.form.person_in_charge_id))) {
                            results.unshift(existing);
                        }
                    }
                    this.teacherGroupOptions = results;
                })
                .catch(() => {})
                .finally(() => {
                    this.loadingTeacherGroups = false;
                });
        },
        onStartDateChange() {
            if (this.$refs.formRef && this.form.end_date) {
                this.$refs.formRef.validateField("end_date");
            }
        },
        fetchJobCatalogs() {
            this.loadingCatalogs = true;
            this.$store
                .dispatch(RO_GET_JOB_CATALOG)
                .then((list) => {
                    this.jobCatalogs = Array.isArray(list) ? list : [];
                })
                .catch(() => {
                    this.jobCatalogs = [
                        {
                            id: 1,
                            name: "Giáo viên IM HN-HCM (Offline)",
                            has_details: true,
                            allow_custom_title: false,
                            details: [
                                { id: 1, category_id: 1, name: "Giáo viên IM HN/HCM - Freetime" },
                                { id: 2, category_id: 1, name: "Giáo viên IM HN/HCM - Part Time 4 Buổi" },
                                { id: 3, category_id: 1, name: "Giáo viên IM HN/HCM - Part Time 6 Buổi" },
                                { id: 4, category_id: 1, name: "Giáo viên IM HN/HCM - Fulltime 4 Buổi" },
                                { id: 5, category_id: 1, name: "Giáo viên IM HN/HCM - Fulltime 6 Buổi" },
                            ],
                        },
                        {
                            id: 2,
                            name: "Giáo viên IM Tỉnh (Offline)",
                            has_details: true,
                            allow_custom_title: false,
                            details: [
                                { id: 6, category_id: 2, name: "Giáo viên IM tại Tỉnh - Freetime" },
                                { id: 7, category_id: 2, name: "Giáo viên IM tại Tỉnh - Part Time 4 Buổi" },
                                { id: 8, category_id: 2, name: "Giáo viên IM tại Tỉnh - Part Time 6 Buổi" },
                                { id: 9, category_id: 2, name: "Giáo viên IM tại Tỉnh - Fulltime 4 Buổi" },
                                { id: 10, category_id: 2, name: "Giáo viên IM tại Tỉnh - Fulltime 6 Buổi" },
                            ],
                        },
                        {
                            id: 3,
                            name: "Giáo viên IM Online",
                            has_details: true,
                            allow_custom_title: false,
                            details: [
                                { id: 11, category_id: 3, name: "Giáo viên IM Online - Freetime" },
                                { id: 12, category_id: 3, name: "Giáo viên IM Online - Part Time 4 Buổi" },
                                { id: 13, category_id: 3, name: "Giáo viên IM Online - Part Time 6 Buổi" },
                                { id: 14, category_id: 3, name: "Giáo viên IM Online - Fulltime 6 Buổi" },
                            ],
                        },
                        {
                            id: 4,
                            name: "Giáo viên Classup",
                            has_details: true,
                            allow_custom_title: false,
                            details: [
                                { id: 15, category_id: 4, name: "Giáo viên Classup Freetime" },
                                { id: 16, category_id: 4, name: "Giáo viên Classup Part Time 4 Buổi" },
                                { id: 17, category_id: 4, name: "Giáo viên Classup Part Time 6 Buổi" },
                                { id: 18, category_id: 4, name: "Giáo viên Classup Fulltime 6 Buổi" },
                            ],
                        },
                        { id: 5, name: "Giáo viên cấp 2", has_details: false, allow_custom_title: true, details: [] },
                        { id: 6, name: "Back Office Offline", has_details: false, allow_custom_title: true, details: [] },
                        { id: 7, name: "Back Office Online", has_details: false, allow_custom_title: true, details: [] },
                        { id: 8, name: "Nhà hàng", has_details: false, allow_custom_title: true, details: [] },
                        { id: 9, name: "Sales", has_details: false, allow_custom_title: true, details: [] },
                        { id: 10, name: "Sales Leader", has_details: false, allow_custom_title: true, details: [] },
                        { id: 11, name: "GĐVP", has_details: false, allow_custom_title: true, details: [] },
                    ];
                })
                .finally(() => {
                    this.loadingCatalogs = false;
                });
        },
        fetchBranches() {
            this.loadingBranches = true;
            this.$store
                .dispatch(RO_GET_BRANCHES)
                .then((list) => {
                    this.branchOptions = Array.isArray(list) ? list : [];
                })
                .catch(() => {
                    this.branchOptions = [];
                })
                .finally(() => {
                    this.loadingBranches = false;
                });
        },
        fetchWorkForms() {
            this.loadingWorkForms = true;
            this.$store
                .dispatch(RO_GET_WORK_FORMS)
                .then((list) => {
                    this.workForms = Array.isArray(list) ? list : [];
                })
                .catch(() => {
                    this.workForms = [];
                })
                .finally(() => {
                    this.loadingWorkForms = false;
                });
        },
        branchLabel(b) {
            if (!b) return "";
            const name = b.name || b.branch_name || b.title || `Cơ sở #${b.id}`;
            return b.address ? `${name} (${b.address})` : name;
        },
        onCategoryChange(categoryId) {
            this.form.recruitment_job_category_id = categoryId || null;
            this.form.recruitment_job_detail_id = null;
            this.form.recruitment_job_detail_name = "";

            if (!categoryId) return;

            const category = this.jobCatalogs.find((c) => String(c.id) === String(categoryId));
            if (!category) return;

            const catName = category.name || "";
            const isTeacher = catName.toLowerCase().includes("giáo viên");

            // Chỉ gợi ý đổi loại tuyển dụng & tự động điền tiêu đề khi KHÔNG PHẢI đang chọn Tuyển hộ
            if (Number(this.form.type) !== TYPE_TUYEN_HO) {
                const oldType = this.form.type;
                if (isTeacher && this.allowTeacherType) {
                    this.form.type = TYPE_TUYEN_HO;
                } else {
                    this.form.type = TYPE_TUYEN_CHINH_THUC;
                }
                if (Number(oldType) !== Number(this.form.type)) {
                    this.validateOrResetWorkType();
                }

                // Gợi ý hình thức từ tên danh mục
                const catNameLower = catName.toLowerCase();
                if (this.form.work_type === null) {
                    const matched = this.filteredWorkForms.find((wf) =>
                        (catNameLower.includes("online") && (wf.name || "").toLowerCase().includes("online")) ||
                        (catNameLower.includes("offline") && (wf.name || "").toLowerCase().includes("offline"))
                    );
                    if (matched) {
                        this.form.work_type = matched.id;
                    }
                }

            }

            if (this.$refs.formRef) this.$refs.formRef.clearValidate();
        },
        onJobDetailChange(detailId) {
            if (!detailId) {
                this.form.recruitment_job_detail_id = null;
                this.form.recruitment_job_detail_name = "";
                return;
            }
            const category = this.selectedCategory;
            const detail = category && category.details
                ? category.details.find((d) => String(d.id) === String(detailId))
                : null;

            if (detail) {
                this.form.recruitment_job_detail_id = detail.id;
                this.form.recruitment_job_detail_name = detail.name;

                // Tự động gợi ý hình thức làm việc từ tên chi tiết
                const dName = (detail.name || "").toLowerCase();
                const matched = this.filteredWorkForms.find((wf) =>
                    (dName.includes("online") && (wf.name || "").toLowerCase().includes("online")) ||
                    (dName.includes("offline") && (wf.name || "").toLowerCase().includes("offline"))
                );
                if (matched) {
                    this.form.work_type = matched.id;
                }
            }
            if (this.$refs.formRef) this.$refs.formRef.clearValidate();
        },
        onJobDetailNameInput(val) {
            this.form.recruitment_job_detail_id = null;
        },
        userOptionLabel(u) {
            if (!u) return "";
            const extra = u.department_name || u.team_name || u.email;
            return extra ? `${u.name} (${extra})` : u.name;
        },
        searchUsers(query) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.fetchUsers(query);
            }, 300);
        },
        fetchUsers(keyword) {
            this.searchingUsers = true;
            this.$store
                .dispatch(RO_GET_TEACHER_GROUPS, { keyword })
                .then((list) => {
                    const results = Array.isArray(list) ? list : [];
                    if (this.form.person_in_charge_id) {
                        const existing = this.userOptions.find((u) => String(u.id) === String(this.form.person_in_charge_id));
                        if (existing && !results.some((u) => String(u.id) === String(this.form.person_in_charge_id))) {
                            results.unshift(existing);
                        }
                    }
                    if (this.form.cv_approver_id) {
                        const existingApprover = this.userOptions.find((u) => String(u.id) === String(this.form.cv_approver_id));
                        if (existingApprover && !results.some((u) => String(u.id) === String(this.form.cv_approver_id))) {
                            results.unshift(existingApprover);
                        }
                    }
                    this.userOptions = results;
                })
                .catch(() => {})
                .finally(() => {
                    this.searchingUsers = false;
                });
        },
        submit() {
            this.$refs.formRef.validate((valid, invalidFields) => {
                if (!valid) {
                    if (invalidFields) {
                        const firstKey = Object.keys(invalidFields)[0];
                        const err = invalidFields[firstKey] && invalidFields[firstKey][0];
                        if (err && err.message) {
                            this.$message.warning(err.message);
                        }
                    }
                    return;
                }
                const type = Number(this.form.type);
                const isNewTeacherOrder = type === TYPE_TUYEN_HO && !this.isEdit;

                // Validate bắt buộc nhập mã đơn đối với job tuyển hộ
                if (type === TYPE_TUYEN_HO && (!this.form.code_order || !String(this.form.code_order).trim())) {
                    this.$message.warning("Vui lòng nhập Mã đơn đối với job tuyển hộ");
                    return;
                }

                // Validate thêm cho chắc chắn: Thời gian kết thúc không được nhỏ hơn thời gian bắt đầu
                if (type === TYPE_TUYEN_HO && this.form.start_date && this.form.end_date) {
                    if (new Date(this.form.end_date).getTime() < new Date(this.form.start_date).getTime()) {
                        this.$message.warning("Thời gian kết thúc không được nhỏ hơn thời gian bắt đầu");
                        return;
                    }
                }

                const detailId = this.hasJobDetails ? (this.form.recruitment_job_detail_id || null) : null;
                const detailName = !this.hasJobDetails
                    ? (this.form.recruitment_job_detail_name ? this.form.recruitment_job_detail_name.trim() : null)
                    : (this.form.recruitment_job_detail_name || null);

                const workTypeVal = this.form.work_type !== null && this.form.work_type !== undefined && this.form.work_type !== ""
                    ? Number(this.form.work_type)
                    : null;

                this.$emit("submit", {
                    id: this.form.id,
                    title: this.form.title.trim(),
                    description: this.form.description ? this.form.description.trim() : null,
                    type: !this.allowTeacherType && isNewTeacherOrder ? TYPE_TUYEN_CHINH_THUC : type,
                    recruitment_job_category_id: this.form.recruitment_job_category_id || null,
                    recruitment_job_detail_id: detailId,
                    recruitment_job_detail_name: detailName,
                    branch_id: this.form.branch_id || null,
                    work_type: workTypeVal,
                    start_date: this.form.start_date || null,
                    end_date: this.form.end_date || null,
                    quantity: this.form.quantity ? Number(this.form.quantity) : 1,
                    code_order: this.form.code_order ? String(this.form.code_order).trim() : null,
                    person_in_charge_id: this.isTeacherOrder
                        ? (this.form.person_in_charge_id || null)
                        : (this.canAssignPersonInCharge
                            ? (this.form.person_in_charge_id || null)
                            : (this.orderData ? (this.orderData.person_in_charge_id || (this.orderData.person_in_charge && this.orderData.person_in_charge.id) || null) : null)),
                    cv_approver_id: this.isTeacherOrder && this.form.cv_approver_id ? Number(this.form.cv_approver_id) : null,
                    hiring_start_date: this.form.hiring_start_date || (this.orderData ? this.orderData.hiring_start_date : null),

                    // Compatibility aliases
                    job_catalog_id: this.form.recruitment_job_category_id || null,
                    job_detail_id: detailId,
                    job_detail: detailName,
                    start_time: this.form.start_date || null,
                    end_time: this.form.end_date || null,
                    amount: this.form.quantity ? Number(this.form.quantity) : 1,
                    working_form: workTypeVal,
                });
            });
        },
        handleApprove(role, status) {
            this.$emit("approve-order", { order: this.orderData, role, status });
            this.$emit("teamlead-approve", { order: this.orderData, role, status, statusVal: status });
        },
        handleTeamleadApprove(status) {
            this.handleApprove("teamlead", status);
        },
    },
};
</script>

<style scoped>
/* ==========================================================================
   Header Styling
   ========================================================================== */
.ro-dialog-header {
    display: flex;
    align-items: center;
    gap: 12px;
}
.ro-dialog-header__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
    color: #4f46e5;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
}
.ro-dialog-header__icon.is-edit {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #d97706;
}
.ro-dialog-header__info {
    flex: 1;
    min-width: 0;
}
.ro-dialog-header__title {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
}
.ro-dialog-header__subtitle {
    margin: 2px 0 0 0;
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
}

/* ==========================================================================
   Form Sectioning & Layout
   ========================================================================== */
.ro-form {
    padding: 4px 2px;
}
::v-deep .ro-form .el-form-item__label,
.ro-form ::v-deep .el-form-item__label {
    padding-bottom: 2px !important;
    line-height: 20px !important;
    font-size: 13px;
    font-weight: 600;
    color: #334155;
}

.ro-form-section {
    background: #ffffff;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    transition: border-color 0.2s;
}
.ro-form-section:hover {
    border-color: #e2e8f0;
}
.ro-form-section--highlight {
    background: #f8fafc;
    border-color: #e2e8f0;
}
.ro-form-section__head {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13.5px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f5f9;
}
.ro-form-section__head i {
    color: #4f46e5;
    font-size: 15px;
}
.mb-0 {
    margin-bottom: 0 !important;
}

/* ==========================================================================
   Type Grid (Radio Card Selector)
   ========================================================================== */
.ro-type-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}
.ro-type-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
}
.ro-type-card:hover {
    border-color: #cbd5e1;
    background: #ffffff;
    transform: translateY(-1px);
}
.ro-type-card.is-active {
    border-color: #4f46e5;
    background: #f5f7ff;
    box-shadow: 0 0 0 1px #4f46e5, 0 4px 12px rgba(79, 70, 229, 0.08);
}
.ro-type-card.is-active.is-teacher {
    border-color: #d97706;
    background: #fffbeb;
    box-shadow: 0 0 0 1px #d97706, 0 4px 12px rgba(217, 119, 6, 0.08);
}
.ro-type-card__icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
    transition: all 0.2s;
}
.ro-type-card.is-active .ro-type-card__icon {
    background: #4f46e5;
    color: #ffffff;
    border-color: #4f46e5;
}
.ro-type-card.is-active.is-teacher .ro-type-card__icon {
    background: #d97706;
    color: #ffffff;
    border-color: #d97706;
}
.ro-type-card__content {
    flex: 1;
    min-width: 0;
}
.ro-type-card__title {
    font-weight: 700;
    font-size: 13.5px;
    color: #1e293b;
    line-height: 1.3;
}
.ro-type-card.is-active .ro-type-card__title {
    color: #4f46e5;
}
.ro-type-card.is-active.is-teacher .ro-type-card__title {
    color: #b45309;
}
.ro-type-card__desc {
    font-size: 11.5px;
    color: #64748b;
    margin-top: 2px;
    line-height: 1.3;
}
.ro-type-card__badge {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: transparent;
    border: 1.5px solid #cbd5e1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: transparent;
    transition: all 0.2s;
}
.ro-type-card.is-active .ro-type-card__badge {
    background: #4f46e5;
    border-color: #4f46e5;
    color: #ffffff;
}
.ro-type-card.is-active.is-teacher .ro-type-card__badge {
    background: #d97706;
    border-color: #d97706;
    color: #ffffff;
}

/* ==========================================================================
   Input / Select / Form Extras
   ========================================================================== */
.ro-catalog-badge {
    float: right;
    color: #4f46e5;
    font-size: 11px;
    font-weight: 600;
    background: #eef2ff;
    border-radius: 4px;
    padding: 1px 6px;
    margin-top: 5px;
}
.ro-user-sub {
    float: right;
    color: #94a3b8;
    font-size: 11.5px;
    margin-left: 8px;
}
.ro-branch-opt {
    display: flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    min-width: 0;
}
.ro-branch-opt__name {
    font-weight: 500;
    color: #1e293b;
    flex-shrink: 0;
    max-width: 45%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.ro-branch-opt__address {
    color: #64748b;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}


/* ==========================================================================
   Dialog Footer
   ========================================================================== */
.ro-dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}
.ro-dialog-footer__right {
    display: flex;
    align-items: center;
    gap: 8px;
}
.ro-btn-cancel {
    border-color: #cbd5e1;
    color: #475569;
}
.ro-btn-cancel:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #0f172a;
}
.ro-btn-submit {
    font-weight: 600;
    padding: 9px 18px;
    box-shadow: 0 2px 6px rgba(79, 70, 229, 0.25);
}
</style>
